import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import ReactEcharts from 'echarts-for-react'

/**
 * @description Stacked bar chart with monthly token consumption. When a month exceeds its token
 * limit, the portion within limit renders in blue and the excess (`exceedanceTokens`) renders in
 * red, stacked on top of it. Tooltip breaks down used/limit/exceedance for both tokens and cost.
 * @param {{ data: Array<{name:string, baseTokens:number, exceedanceTokens:number, tokens:number, tokenLimit:number, cost:number, exceedanceCost:number, costLimit:number}>, height?:string|number, currency?:string }} props
 */
function AssistantConsumptionGraph(props) {
  const { data, height = '100%', currency = 'MXN' } = props
  const { t, i18n } = useTranslation('clients-dashboard', { keyPrefix: 'globalBillingScreen' })

  const formatCurrency = (value) => {
    const num = Number(value)
    if (!Number.isFinite(num)) return 'N/A'
    return num.toLocaleString(i18n.language === 'en' ? 'en-US' : 'es-MX', { style: 'currency', currency, maximumFractionDigits: 0 })
  }

  const formatCompactNumber = (value) => {
    const num = Number(value)
    if (!Number.isFinite(num)) return 'N/A'
    if (Math.abs(num) >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
    if (Math.abs(num) >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
    return num.toString()
  }

  const option = useMemo(() => {
    if (!data || !data.length) return

    return {
      grid: { top: 16, right: 12, bottom: 24, containLabel: true },
      xAxis: { type: 'category', axisTick: { show: false } },
      yAxis: { type: 'value', axisLabel: { formatter: formatCompactNumber } },
      dataset: {
        source: data,
        dimensions: ['name', 'baseTokens', 'exceedanceTokens', 'tokens', 'tokenLimit', 'cost', 'exceedanceCost', 'costLimit']
      },
      series: [
        {
          name: t('assistantConsumption.seriesName'),
          type: 'bar',
          stack: 'consumption',
          barWidth: 32,
          encode: { x: 'name', y: 'baseTokens' },
          itemStyle: { color: '#00A4E0' }
        },
        {
          name: t('assistantConsumption.exceedanceSeriesName'),
          type: 'bar',
          stack: 'consumption',
          barWidth: 32,
          encode: { x: 'name', y: 'exceedanceTokens' },
          itemStyle: { color: '#EF4444', borderRadius: [4, 4, 0, 0] }
        }
      ],
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const item = params[0]?.value
          if (!item) return ''
          const lines = [`<strong>${item.name}</strong>`]

          if (Number.isFinite(item.tokenLimit)) {
            lines.push(`${t('assistantConsumption.tooltipTokensLimit')}: <strong>${Number(item.tokenLimit).toLocaleString()}</strong>`)
          }
          lines.push(`${t('assistantConsumption.tooltipTokensUsed')}: <strong>${Number(item.tokens).toLocaleString()}</strong>`)
          
          if (item.exceedanceTokens) {
            lines.push(`${t('assistantConsumption.tooltipTokensExceedance')}: <strong>${Number(item.exceedanceTokens).toLocaleString()}</strong>`)
          }

          if (Number.isFinite(item.costLimit)) {
            lines.push(`${t('assistantConsumption.tooltipCostLimit')}: <strong>${formatCurrency(item.costLimit)}</strong>`)
          }
          lines.push(`${t('assistantConsumption.tooltipCostUsed')}: <strong>${formatCurrency(item.cost)}</strong>`)
          
          if (item.exceedanceCost) {
            lines.push(`${t('assistantConsumption.tooltipCostExceedance')}: <strong>${formatCurrency(item.exceedanceCost)}</strong>`)
          }

          return lines.join('<br/>')
        }
      }
    }
  }, [data, t, i18n.language, currency]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="dashboardView-card dashboard-graph-card">
      <p className="dashboardView-card-title">{t('assistantConsumption.title')}</p>
      {option ? <ReactEcharts option={option} style={{ height }} /> : <p>N/D</p>}
    </div>
  )
}

export default AssistantConsumptionGraph
